import type { ViewState } from '../shared/types';
import type { Place } from '../types/warehouse.types';
import type { BenchmarkConfig } from './MetricsCollector';

export interface BenchmarkContext {
  viewState: ViewState;
  setViewState: React.Dispatch<React.SetStateAction<ViewState>>;
  places: Map<string, Place>;
  canvasSize: { width: number; height: number };
  setSelectedPlaceIds: (ids: Set<string>) => void;
  config: BenchmarkConfig;
  zoomIn: () => void;
  zoomOut: () => void;
  handleRotateLeftMouseDown: () => void;
  handleRotateRightMouseDown: () => void;
  handleRotateMouseUp: () => void;
}

export interface BenchmarkOperation {
  id: string;
  name: string;
  execute: (context: BenchmarkContext) => Promise<void>;
}

type TranslateFn = (key: string, options?: any) => string;

const getDetail = (translate: TranslateFn, withSelection: boolean, selectedPlaces?: string[]): string =>
  withSelection
    ? translate('osfc.storage_ui.benchmark.op.n_selected', { count: selectedPlaces?.length ?? 0 })
    : translate('osfc.storage_ui.benchmark.op.none_selected');

export const createRotationOperation = (
  withSelection: boolean,
  selectedPlaces: string[] | undefined,
  translate: TranslateFn,
): BenchmarkOperation => ({
  id: withSelection ? 'rotation-selected' : 'rotation-base',
  name: translate('osfc.storage_ui.benchmark.op.rotation', { detail: getDetail(translate, withSelection, selectedPlaces) }),

  execute: async ({
    setSelectedPlaceIds,
    config,
    handleRotateLeftMouseDown,
    handleRotateMouseUp,
  }: BenchmarkContext): Promise<void> => {
    // Apply selection if needed
    if (withSelection && selectedPlaces) {
      setSelectedPlaceIds(new Set(selectedPlaces));
      await new Promise(resolve => setTimeout(resolve, 100));
    } else if (!withSelection) {
      setSelectedPlaceIds(new Set());
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Start rotation using standard rotation control
    handleRotateLeftMouseDown();

    // Wait for the rotation duration
    await new Promise(resolve => setTimeout(resolve, config.rotationDuration));

    // Stop rotation
    handleRotateMouseUp();
  }
});

export const createZoomOperation = (
  withSelection: boolean,
  selectedPlaces: string[] | undefined,
  translate: TranslateFn,
): BenchmarkOperation => ({
  id: withSelection ? 'zoom-selected' : 'zoom-base',
  name: translate('osfc.storage_ui.benchmark.op.zoom', { detail: getDetail(translate, withSelection, selectedPlaces) }),

  execute: async ({
    setSelectedPlaceIds,
    zoomIn,
    zoomOut,
  }: BenchmarkContext): Promise<void> => {
    // Apply selection if needed
    if (withSelection && selectedPlaces) {
      setSelectedPlaceIds(new Set(selectedPlaces));
      await new Promise(resolve => setTimeout(resolve, 100));
    } else if (!withSelection) {
      setSelectedPlaceIds(new Set());
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Calculate number of steps needed (each zoomIn multiplies scale by 1.2)
    // To go from 1.0 to ~3.0: 1.2^n = 3 => n = log(3) / log(1.2) ≈ 6 steps
    const zoomInSteps = 10;
    const stepDelay = 150; // Smooth zoom with 150ms between steps

    // Zoom in
    for (let i = 0; i < zoomInSteps; i++) {
      zoomIn();
      await new Promise(resolve => setTimeout(resolve, stepDelay));
    }

    // Zoom out
    for (let i = 0; i < zoomInSteps; i++) {
      zoomOut();
      await new Promise(resolve => setTimeout(resolve, stepDelay));
    }
  }
});

export const createPanOperation = (
  withSelection: boolean,
  selectedPlaces: string[] | undefined,
  translate: TranslateFn,
): BenchmarkOperation => ({
  id: withSelection ? 'pan-selected' : 'pan-base',
  name: translate('osfc.storage_ui.benchmark.op.pan', { detail: getDetail(translate, withSelection, selectedPlaces) }),

  execute: async ({
    setSelectedPlaceIds,
    viewState,
    setViewState,
    config,
  }: BenchmarkContext): Promise<void> => {
    // Apply selection if needed
    if (withSelection && selectedPlaces) {
      setSelectedPlaceIds(new Set(selectedPlaces));
      await new Promise(resolve => setTimeout(resolve, 100));
    } else if (!withSelection) {
      setSelectedPlaceIds(new Set());
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    const startOffsetX = viewState.offsetX;
    const startOffsetY = viewState.offsetY;
    const radius = config.panDistance;
    const duration = config.panDuration;
    const startTime = performance.now();

    return new Promise<void>((resolve) => {
      const animate = (timestamp: number) => {
        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Circular pan pattern
        const angle = progress * 2 * Math.PI;
        const offsetX = startOffsetX + radius * Math.cos(angle);
        const offsetY = startOffsetY + radius * Math.sin(angle);

        setViewState((prev) => ({
          ...prev,
          offsetX,
          offsetY
        }));

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          resolve();
        }
      };

      requestAnimationFrame(animate);
    });
  }
});

export const createRapidZoomOperation = (
  withSelection: boolean,
  selectedPlaces: string[] | undefined,
  translate: TranslateFn,
): BenchmarkOperation => ({
  id: withSelection ? 'rapid-zoom-selected' : 'rapid-zoom-base',
  name: translate('osfc.storage_ui.benchmark.op.rapid_zoom', { detail: getDetail(translate, withSelection, selectedPlaces) }),

  execute: async ({
    setSelectedPlaceIds,
    config,
    zoomIn,
    zoomOut,
  }: BenchmarkContext): Promise<void> => {
    // Apply selection if needed
    if (withSelection && selectedPlaces) {
      setSelectedPlaceIds(new Set(selectedPlaces));
      await new Promise(resolve => setTimeout(resolve, 100));
    } else if (!withSelection) {
      setSelectedPlaceIds(new Set());
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    const steps = config.rapidZoomSteps;
    const stepDelay = config.rapidZoomStepDelay;

    // Zoom in - using zoomIn() function (reuses existing logic)
    for (let i = 0; i < steps; i++) {
      zoomIn();
      await new Promise(resolve => setTimeout(resolve, stepDelay));
    }

    // Zoom out - using zoomOut() function (reuses existing logic)
    for (let i = 0; i < steps; i++) {
      zoomOut();
      await new Promise(resolve => setTimeout(resolve, stepDelay));
    }
  }
});
